import request from '@/api/request'
import axios from 'axios'

export default {
    /**
     * 上传音频文件
     * @param {File} file - 音频文件对象
     * @param {Function} onProgress - 进度回调函数 (percent: number) => void
     * @returns {Promise}
     */
    uploadAudio(file, onProgress) {
        const formData = new FormData();
        formData.append('file', file);

        return request({
            url: '/voice/uploadAudio',
            method: 'post',
            data: formData,
            onUploadProgress: (progressEvent) => {
                if (onProgress && progressEvent.total > 0) {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percent, progressEvent);
                }
            }
        });
    },
    /**
     * 创建声音 - 使用 /voice/createVoice 接口（用户克隆声音）
     * @param {Object} params - 参数对象
     * @param {String} params.voiceName - 声音名称
     * @param {String} params.fileUrl - 文件URL
     * @param {Number} params.speechRate - 语速 (Double)
     * @param {Number} params.pitchRate - 音调 (Double)
     * @param {String} params.language - 语言代码 (String) zh/en/ja/ko/fr/es/it/de
     * @returns {Promise}
     */
    createVoice(params) {
        const formData = new FormData();
        formData.append('voiceName', params.voiceName);
        formData.append('fileUrl', params.fileUrl);
        if (params.speechRate !== undefined && params.speechRate !== null) {
            formData.append('speechRate', params.speechRate);
        }
        if (params.pitchRate !== undefined && params.pitchRate !== null) {
            formData.append('pitchRate', params.pitchRate);
        }
        if (params.language) {
            formData.append('language', params.language);
        }
        if (params.groupId !== undefined && params.groupId !== null) {
            formData.append('groupId', params.groupId);
        }

        return request({
            url: '/voice/createVoice',
            method: 'post',
            data: formData
        });
    },
    /**
     * 创建系统声音 - 使用 /voice/system/createVoice 接口（管理员）
     * @param {Object} params - 参数对象
     * @param {String} params.voiceName - 声音名称
     * @param {String} params.fileUrl - 文件URL
     * @param {Number} params.speechRate - 语速 (Double, 可选)
     * @param {Number} params.pitchRate - 音调 (Double, 可选)
     * @returns {Promise}
     */
    createSystemVoice(params) {
        const formData = new FormData();
        formData.append('voiceName', params.voiceName);
        if (params.fileUrl) {
            formData.append('fileUrl', params.fileUrl);
        }
        if (params.voiceId) {
            formData.append('voiceId', params.voiceId);
        }
        if (params.speechRate !== undefined && params.speechRate !== null) {
            formData.append('speechRate', params.speechRate);
        }
        if (params.pitchRate !== undefined && params.pitchRate !== null) {
            formData.append('pitchRate', params.pitchRate);
        }
        if (params.groupId !== undefined && params.groupId !== null) {
            formData.append('groupId', params.groupId);
        }
        if (params.language) {
            formData.append('language', params.language);
        }

        return request({
            url: '/voice/system/createVoice',
            method: 'post',
            data: formData
        });
    },
    /**
     * 获取系统声音列表
     * @param {Object} params - 参数对象
     * @param {Number} params.groupId - 分组ID (1=国内, 2=国外)
     * @returns {Promise}
     */
    getSystemVoices(params) {
        return request({
            url: '/voice/getSystemVoices',
            method: 'get',
            params: params || {}
        });
    },
    /**
     * 获取用户声音列表
     * @returns {Promise}
     */
    getUserVoices() {
        return request({
            url: '/voice/getUserVoices',
            method: 'get'
        });
    },
    /**
     * 检查声音名称是否重复
     * @param {Object} params - 参数对象
     * @param {String} params.voiceName - 声音名称
     * @returns {Promise}
     */
    checkVoiceName(params) {
        return request({
            url: '/voice/checkVoiceName',
            method: 'get',
            params: {
                voiceName: params.voiceName
            }
        });
    },
    /**
     * 通过声音ID创建声音
     * @param {Object} params - 参数对象
     * @param {String} params.voiceName - 声音名称
     * @param {String} params.voiceId - 声音ID
     * @param {Number} params.speechRate - 语速 (Double)
     * @param {Number} params.pitchRate - 音调 (Double)
     * @param {Number} params.systemVoiceId - 系统声音ID (Long)
     * @param {String} params.language - 语言代码 (String) zh/en/ja/ko/fr/es/it/de
     * @returns {Promise}
     */
    createVoiceByVoiceId(params) {
        const formData = new FormData();
        formData.append('voiceName', params.voiceName);
        formData.append('voiceId', params.voiceId || '');
        formData.append('speechRate', params.speechRate);
        formData.append('pitchRate', params.pitchRate);
        if (params.systemVoiceId !== undefined && params.systemVoiceId !== null) {
            formData.append('systemVoiceId', params.systemVoiceId);
        }
        if (params.language) {
            formData.append('language', params.language);
        }
        if (params.groupId !== undefined && params.groupId !== null) {
            formData.append('groupId', params.groupId);
        }

        return request({
            url: '/voice/createVoiceByVoiceId',
            method: 'post',
            data: formData
        });
    },
    /**
     * 预览声音 - TTS接口 (文本转语音)
     * @param {Object} params - 参数对象
     * @param {String} params.voiceId - 声音ID
     * @param {String} params.text - 预览文本
     * @param {Number} params.pitchRate - 音调 (Double, 可选)
     * @param {Number} params.speechRate - 语速 (Double, 可选)
     * @param {String} params.language - 语言代码 (String, 可选) zh/en/ja/ko/fr/es/it/de
     * @returns {Promise} 返回音频Blob对象
     */
    async previewVoice(params) {
        const formData = new FormData();
        formData.append('text', params.text);
        formData.append('voiceId', params.voiceId);
        
        // 可选参数
        if (params.speechRate !== undefined && params.speechRate !== null) {
            formData.append('speechRate', params.speechRate);
        }
        if (params.pitchRate !== undefined && params.pitchRate !== null) {
            formData.append('pitchRate', params.pitchRate);
        }
        if (params.language) {
            formData.append('language', params.language);
        }
        if (params.groupId !== undefined && params.groupId !== null) {
            formData.append('groupId', params.groupId);
        }

        // 获取token
        const store = require('@/store').default;
        const token = store.state.token || '';

        try {
            // 使用 request 包装器，因为后端返回 ResponseDTO 格式
            // 先尝试作为 JSON 处理
            const response = await axios({
                url: '/api/laplace/voice/synthesize',
                method: 'post',
                data: formData,
                responseType: 'json', // 先尝试 JSON
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // 检查响应格式
            if (response.data && typeof response.data === 'object') {
                // 返回的是 ResponseDTO 格式
                if (response.data.statusCode === 100200 && response.data.data) {
                    // data 字段包含音频数据（字节数组）
                    let audioBlob;
                    const audioData = response.data.data;
                    
                    if (Array.isArray(audioData)) {
                        // 如果是数组，转换为 Uint8Array
                        audioBlob = new Blob([new Uint8Array(audioData)], { type: 'audio/pcm' });
                    } else if (typeof audioData === 'string') {
                        // 如果是 base64 字符串，需要解码
                        try {
                            const binaryString = atob(audioData);
                            const bytes = new Uint8Array(binaryString.length);
                            for (let i = 0; i < binaryString.length; i++) {
                                bytes[i] = binaryString.charCodeAt(i);
                            }
                            audioBlob = new Blob([bytes], { type: 'audio/pcm' });
                        } catch (e) {
                            // 如果不是 base64，尝试直接使用
                            audioBlob = new Blob([audioData], { type: 'audio/pcm' });
                        }
                    } else {
                        // 其他情况，尝试直接使用
                        audioBlob = new Blob([audioData], { type: 'audio/pcm' });
                    }
                    
                    return {
                        statusCode: 100200,
                        data: audioBlob,
                        message: response.data.message || '合成成功'
                    };
                } else {
                    return {
                        statusCode: response.data.statusCode || 400,
                        message: response.data.message || '合成失败',
                        data: null
                    };
                }
            } else {
                // 如果不是 JSON，可能是二进制流，需要重新请求
                const binaryResponse = await axios({
                    url: '/api/laplace/voice/synthesize',
                    method: 'post',
                    data: formData,
                    responseType: 'arraybuffer',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                const audioBlob = new Blob([binaryResponse.data], { type: 'audio/pcm' });
                return {
                    statusCode: 100200,
                    data: audioBlob,
                    message: '合成成功'
                };
            }
        } catch (error) {
            // 处理错误响应
            if (error.response) {
                if (error.response.data && typeof error.response.data === 'object' && error.response.data.statusCode) {
                    // ResponseDTO 格式的错误
                    throw {
                        statusCode: error.response.data.statusCode,
                        message: error.response.data.message || '合成失败',
                        data: null
                    };
                } else {
                    throw {
                        statusCode: error.response.status,
                        message: '合成失败',
                        data: null
                    };
                }
            }
            throw error;
        }
    },
    /**
     * 删除声音
     * @param {Object} params - 参数对象
     * @param {Number} params.id - 声音ID (Long)
     * @returns {Promise}
     */
    deleteVoice(params) {
        return request({
            url: '/voice/deleteVoice',
            method: 'post',
            params: {
                id: params.id,
                groupId: params.groupId
            }
        });
    },
    /**
     * 删除系统声音（管理员）
     * @param {Object} params - 参数对象
     * @param {Number} params.id - 声音ID (Long)
     * @returns {Promise}
     */
    deleteSystemVoice(params) {
        return request({
            url: '/voice/system/deleteVoice',
            method: 'post',
            params: {
                id: params.id,
                groupId: params.groupId
            }
        });
    },
    /**
     * 更新用户声音
     * @param {Object} params - 参数对象
     * @param {Number} params.id - 声音ID (Long)
     * @param {String} params.voiceName - 声音名称
     * @param {Number} params.speechRate - 语速 (Double, 可选)
     * @param {Number} params.pitchRate - 音调 (Double, 可选)
     * @param {String} params.language - 语言代码 (String, 可选) zh/en/ja/ko/fr/es/it/de
     * @returns {Promise}
     */
    updateVoice(params) {
        const formData = new FormData();
        formData.append('id', params.id);
        formData.append('voiceName', params.voiceName);
        
        if (params.speechRate !== undefined && params.speechRate !== null) {
            formData.append('speechRate', params.speechRate);
        }
        if (params.pitchRate !== undefined && params.pitchRate !== null) {
            formData.append('pitchRate', params.pitchRate);
        }
        if (params.language) {
            formData.append('language', params.language);
        }
        if (params.groupId !== undefined && params.groupId !== null) {
            formData.append('groupId', params.groupId);
        }

        return request({
            url: '/voice/updateVoice',
            method: 'post',
            data: formData
        });
    },
    /**
     * 通过声音ID更新用户声音
     * @param {Object} params - 参数对象
     * @param {Number} params.id - 声音ID (Long)
     * @param {String} params.voiceName - 声音名称
     * @param {Number} params.speechRate - 语速 (Double, 可选)
     * @param {Number} params.pitchRate - 音调 (Double, 可选)
     * @param {String} params.language - 语言代码 (String, 可选) zh/en/ja/ko/fr/es/it/de
     * @returns {Promise}
     */
    updateVoiceByVoiceId(params) {
        const formData = new FormData();
        formData.append('id', params.id);
        formData.append('voiceName', params.voiceName);
        
        if (params.speechRate !== undefined && params.speechRate !== null) {
            formData.append('speechRate', params.speechRate);
        }
        if (params.pitchRate !== undefined && params.pitchRate !== null) {
            formData.append('pitchRate', params.pitchRate);
        }
        if (params.language) {
            formData.append('language', params.language);
        }
        if (params.groupId !== undefined && params.groupId !== null) {
            formData.append('groupId', params.groupId);
        }

        return request({
            url: '/voice/updateVoiceByVoiceId',
            method: 'post',
            data: formData
        });
    },
    /**
     * 更新系统声音
     * @param {Object} params - 参数对象 (Voices 对象)
     * @param {Number} params.id - 声音ID (Long)
     * @param {String} params.voiceName - 声音名称
     * @param {Number} params.gender - 性别 (0-女, 1-男)
     * @param {Number} params.speechRate - 语速 (Double, 可选)
     * @param {Number} params.pitchRate - 音调 (Double, 可选)
     * @returns {Promise}
     */
    updateSystemVoice(params) {
        // 使用 @RequestBody，传递 JSON 对象
        const voiceData = {
            id: params.id,
            voiceName: params.voiceName
        };
        
        if (params.gender !== undefined && params.gender !== null) {
            voiceData.gender = params.gender;
        }
        if (params.speechRate !== undefined && params.speechRate !== null) {
            voiceData.speechRate = params.speechRate;
        }
        if (params.pitchRate !== undefined && params.pitchRate !== null) {
            voiceData.pitchRate = params.pitchRate;
        }
        if (params.groupId !== undefined && params.groupId !== null) {
            voiceData.groupId = params.groupId;
        }

        return request({
            url: '/voice/system/updateVoice',
            method: 'post',
            data: voiceData
        });
    }
}
